import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { ExampleHint } from '../../ui';
import { useAppTranslation } from '../../../i18n/useAppTranslation';
import { integrationApiService, ShopifyFieldOwner, StoreLayoutSettings } from '../../../services/integrationApi';
import { categorySlugCounts, ruleMatchCounts, suggestKeywords } from '../../../utils/mappingIndex';
import {
  FallbackMode,
  categorySlugs,
  fallbackLabel,
  fallbackModeOf,
  resolveTaxonomyForSlug,
  taxonomyLabel,
  taxonomyShortLabel,
  unmappedCategoryIsUnset,
  unmappedFallbackGid,
  withFallbackMode,
} from '../mappingModel';
import CategoryMapStep, { MapFilter } from './CategoryMapStep';
import DiscoveryBanner from './DiscoveryBanner';
import FallbackStep from './FallbackStep';
import MappingExplainer from './MappingExplainer';
import NameRulesStep from './NameRulesStep';
import SummaryCard, { StepSummary } from './SummaryCard';
import TaxonomyStep, { TaxonomyDrafts } from './TaxonomyStep';
import TryProduct, { EMPTY_TRY_INPUTS, TryInputs } from './TryProduct';
import { useTypeCategoryData } from './useTypeCategoryData';
import { useShopifyTaxonomyOptions } from '../useShopifyTaxonomyOptions';
import { visuallyHiddenSx } from './v2Ui';
import MappingSectionLayout, { MappingMenuGroup, MappingMenuItem } from '../MappingSectionLayout';
import { MappingSection, MappingSectionLink, TypeCategorySection, isTypeCategorySection, menuSectionOf } from '../mappingSections';
import { LENS_FOCAL_CATEGORY_KEYS } from '../../../constants/lensFocalCategoryKeys';

type DiscoveredLayout = {
  categoryMap?: Record<string, string>;
  productTypesInUse?: string[];
  collections?: StoreLayoutSettings['collections'];
  menuStructure?: StoreLayoutSettings['menuStructure'];
  allMotzuCategorySlugs?: string[];
};

export interface TypeCategoryViewProps {
  integrationId: string;
  /** The pending (unsaved) layout. The view never keeps its own copy. */
  layout: StoreLayoutSettings | undefined;
  /** Called with the whole next layout on every edit; the parent marks itself dirty. */
  onChange: (layout: StoreLayoutSettings) => void;
  /** Unsaved edits to the store's taxonomy entries, held by the parent so its Save and Discard cover them. */
  taxonomyDrafts: TaxonomyDrafts;
  onTaxonomyDraftsChange: (drafts: TaxonomyDrafts) => void;
  /**
   * The section the URL asks for (`?section=`). The view opens that panel; `try` opens Field ownership
   * and scrolls to Try a product. Without it the view keeps its own selection (Field ownership first).
   */
  section?: MappingSectionLink | null;
  /** Called when the store opens a section, so the page can keep it in its URL. */
  onSection?: (section: MappingSection) => void;
  /**
   * The "Fields & listings" sections (spec metafields, listing matches): their menu item and their
   * panel. The parent owns their state; this view lists them in the menu and shows the open one.
   */
  extraSections?: MappingExtraSection[];
}

export interface MappingExtraSection {
  item: MappingMenuItem;
  content: React.ReactNode;
}

/** Field owner names as the Field ownership menu line says them ("Category: Motzu · Type: Fill blanks"). */
const OWNER_SHORT_KEY: Record<ShopifyFieldOwner, string> = {
  motzu: 'integrations.shopifyMapping.menu.owner.motzu',
  fill_blank: 'integrations.shopifyMapping.menu.owner.fillBlank',
  never: 'integrations.shopifyMapping.menu.owner.never',
};

/** Which panel a summary step row opens. */
const STEP_SECTION: Record<1 | 2 | 3 | 4, TypeCategorySection> = {
  1: 'taxonomy',
  2: 'name-rules',
  3: 'category-map',
  4: 'fallback',
};

const TRY_ANCHOR_ID = 'mapping-try-product';

/** The focusable heading of a section's panel; focus lands here when a switch hides the focused control. */
export const panelHeadingId = (section: MappingSection) => `mapping-panel-heading-${section}`;

const explainerKey = (integrationId: string) => `motzu.mappingV2Explainer.open.${integrationId}`;

const readExplainerOpen = (integrationId: string): boolean => {
  try {
    return window.localStorage.getItem(explainerKey(integrationId)) === '1';
  } catch {
    return false;
  }
};

const writeExplainerOpen = (integrationId: string, open: boolean) => {
  try {
    window.localStorage.setItem(explainerKey(integrationId), open ? '1' : '0');
  } catch {
    // Private mode or blocked storage: the explainer simply starts collapsed next time.
  }
};

const nonEmpty = (values: unknown[]): string[] =>
  values.filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

/**
 * The Mapping tab of a Shopify integration (v2 panels in the v3 layout, MOT-278): a left section menu
 * with a live status line per item, and one panel at a time. Type & category: Field ownership (owners,
 * the decision order as rows that open each step), then each step's own panel. Try a product sits in a
 * sticky column beside every type panel except the overrides table, where it moves under the table.
 * The "Fields & listings" sections come in through `extraSections`.
 */
const TypeCategoryView: React.FC<TypeCategoryViewProps> = ({
  integrationId,
  layout,
  onChange,
  taxonomyDrafts,
  onTaxonomyDraftsChange,
  section,
  onSection,
  extraSections = [],
}) => {
  const { t } = useAppTranslation();
  const current = layout || {};
  const data = useTypeCategoryData(integrationId);
  const { flags, categoryDefaults, registry, taxonomyStats, indexRows, summary } = data;
  // A category outside the curated list (picked from the full taxonomy) is labelled by its breadcrumb in every step.
  const taxonomyOptions = useShopifyTaxonomyOptions(
    data.taxonomyOptions,
    useMemo(
      () => [
        ...(registry?.entries || []).map((entry) => entry.shopifyCategoryGid),
        ...Object.values(taxonomyDrafts || {}).map((draft) => draft.shopifyCategoryGid),
        ...Object.values(current.taxonomyCategoryMap || {}),
        ...(current.productTypeRules || []).map((rule) => rule.taxonomyCategoryGid),
        current.unmappedTaxonomyCategory,
      ],
      [registry, taxonomyDrafts, current.taxonomyCategoryMap, current.productTypeRules, current.unmappedTaxonomyCategory],
    ),
  );

  // One panel at a time. The URL's section wins when there is one; otherwise the view keeps its own.
  const [active, setActive] = useState<MappingSection>(() => menuSectionOf(section));
  const [pendingScroll, setPendingScroll] = useState<'top' | 'try' | null>(section === 'try' ? 'try' : null);
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (section === undefined) return;
    setActive(menuSectionOf(section));
    if (section === 'try') setPendingScroll('try');
  }, [section]);
  useEffect(() => {
    if (!pendingScroll) return undefined;
    const frame = requestAnimationFrame(() => {
      if (pendingScroll === 'try') {
        document.getElementById(TRY_ANCHOR_ID)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      } else if ((rowRef.current?.getBoundingClientRect().top ?? 0) < 0) {
        // The store opened a section after scrolling down: bring the new panel's top into view.
        rowRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      }
      setPendingScroll(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [pendingScroll]);
  // Opening a section from inside a panel (a step row, Re-discover) hides the control that had focus, so
  // focus moves to the opened panel's heading. A menu click keeps focus on its (still visible) menu item.
  const contentRef = useRef<HTMLDivElement>(null);
  const [pendingFocus, setPendingFocus] = useState<MappingSection | null>(null);
  const open = (next: MappingSection, options: { focusPanel?: boolean } = {}) => {
    const focusInPanel = Boolean(contentRef.current?.contains(document.activeElement));
    setActive(next);
    onSection?.(next);
    setPendingScroll('top');
    if (options.focusPanel || focusInPanel) setPendingFocus(next);
  };
  useEffect(() => {
    if (!pendingFocus) return;
    document.getElementById(panelHeadingId(pendingFocus))?.focus({ preventScroll: true });
    setPendingFocus(null);
  }, [pendingFocus]);
  const [tryInputs, setTryInputs] = useState<TryInputs>(EMPTY_TRY_INPUTS);
  const [discovering, setDiscovering] = useState(false);
  const [discovered, setDiscovered] = useState<DiscoveredLayout | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [explainerOpen, setExplainerOpen] = useState<boolean>(() => readExplainerOpen(integrationId));
  useEffect(() => setExplainerOpen(readExplainerOpen(integrationId)), [integrationId]);
  const [filter, setFilter] = useState<MapFilter>('all');
  const [extraSlugs, setExtraSlugs] = useState<string[]>([]);
  // "Use a default type" is chosen before a type is typed; with no type saved it still derives.
  const [defaultPicked, setDefaultPicked] = useState(false);

  const categoryMap = current.categoryMap || {};
  const taxonomyMap = current.taxonomyCategoryMap || {};
  const rules = current.productTypeRules || [];
  const storedMode = fallbackModeOf(current);
  const mode: FallbackMode = storedMode === 'derive' && defaultPicked ? 'default' : storedMode;
  const patch = (partial: Partial<StoreLayoutSettings>) => onChange({ ...current, ...partial });

  const slugs = useMemo(
    () => categorySlugs(current, extraSlugs, flags.lensFocalRangeOn ? LENS_FOCAL_CATEGORY_KEYS : []),
    [current, extraSlugs, flags.lensFocalRangeOn],
  );

  const typeOptions = useMemo(
    () =>
      Array.from(
        new Set(nonEmpty([
          ...(current.productTypesInUse || []),
          ...(discovered?.productTypesInUse || []),
          ...Object.values(categoryMap),
          ...rules.map((rule) => rule.productType),
          current.defaultProductType,
        ])),
      ).sort((a, b) => a.localeCompare(b)),
    [current.productTypesInUse, current.defaultProductType, categoryMap, rules, discovered],
  );

  const suggestions = useMemo(() => {
    const next: Record<string, string> = {};
    for (const [slug, type] of Object.entries(discovered?.categoryMap || {})) {
      if (typeof type === 'string' && type.trim() && categoryMap[slug] !== type) next[slug] = type;
    }
    return next;
  }, [discovered, categoryMap]);
  const suggestionCount = Object.keys(suggestions).length;

  const newSlugOptions = useMemo(() => {
    if (!indexRows) return current.allMotzuCategorySlugs || [];
    const listed = new Set([...slugs, ...Object.keys(categoryMap)]);
    return Array.from(categorySlugCounts(indexRows).entries())
      .filter(([slug]) => !listed.has(slug))
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([slug, count]) => ({ value: slug, hint: `matches ${count} product${count === 1 ? '' : 's'}` }));
  }, [indexRows, slugs, categoryMap, current.allMotzuCategorySlugs]);
  const ruleCounts = useMemo(() => (indexRows ? ruleMatchCounts(indexRows, rules) : null), [indexRows, rules]);
  const keywordSuggestions = useMemo(() => (indexRows ? suggestKeywords(indexRows, rules) : undefined), [indexRows, rules]);

  const discover = async () => {
    setDiscovering(true);
    setError(null);
    try {
      const result = await integrationApiService.discoverStoreLayout(integrationId);
      setDiscovered(result.discovered || {});
      setBannerDismissed(false);
      setFilter('suggested');
      // The suggestions land in the overrides table, so that is where the store goes to review them.
      open('category-map', { focusPanel: true });
    } catch (err: unknown) {
      setError(`Discovery failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDiscovering(false);
    }
  };

  const acceptSuggestions = (onlySlug?: string) => {
    if (!discovered) return;
    const nextMap = { ...categoryMap };
    for (const [slug, type] of Object.entries(suggestions)) {
      if (onlySlug && slug !== onlySlug) continue;
      nextMap[slug] = type;
    }
    patch({
      categoryMap: nextMap,
      productTypesInUse: discovered.productTypesInUse || current.productTypesInUse,
      allMotzuCategorySlugs: discovered.allMotzuCategorySlugs || current.allMotzuCategorySlugs,
      collections: discovered.collections || current.collections,
      menuStructure: discovered.menuStructure || current.menuStructure,
      discoveredAt: new Date().toISOString(),
    });
    if (!onlySlug) {
      setDiscovered(null);
      setBannerDismissed(true);
      setFilter('all');
    }
  };

  const setType = (slug: string, value: string) => {
    const next = { ...categoryMap };
    if (value.trim()) next[slug] = value;
    else delete next[slug];
    patch({ categoryMap: next });
  };
  const setCategory = (slug: string, gid: string) => {
    const next = { ...taxonomyMap };
    if (gid) next[slug] = gid;
    else delete next[slug];
    patch({ taxonomyCategoryMap: next });
  };
  const applyBulk = (targets: string[], type: string, gid: string) => {
    const nextMap = { ...categoryMap };
    const nextTaxonomy = { ...taxonomyMap };
    for (const slug of targets) {
      if (type) nextMap[slug] = type;
      if (gid) nextTaxonomy[slug] = gid;
    }
    patch({ categoryMap: nextMap, taxonomyCategoryMap: nextTaxonomy });
  };
  const resetCategories = () => {
    const { taxonomyCategoryMap: _overrides, ...rest } = current;
    onChange(rest);
  };
  const addSlug = (slug: string) => {
    setExtraSlugs((prev) => (prev.includes(slug) ? prev : [slug, ...prev]));
    const base = current.allMotzuCategorySlugs?.length ? current.allMotzuCategorySlugs : categorySlugs(current);
    if (!base.includes(slug)) patch({ allMotzuCategorySlugs: [slug, ...base] });
    setFilter('all');
  };
  const setMode = (next: FallbackMode) => {
    setDefaultPicked(next === 'default');
    onChange(withFallbackMode(current, next, next === 'default' ? current.defaultProductType : undefined));
  };

  const defaultFor = (slug: string) => {
    const resolved = resolveTaxonomyForSlug(slug, { ...current, taxonomyCategoryMap: {} }, categoryDefaults, {
      unmappedUnset: flags.unmappedUnsetOn,
    });
    // "Don't set" fallback (flag on): a row with no override sends no category, so it shows that, not the neutral root.
    if (resolved?.step === 'unset') return { gid: null, builtIn: false, unset: true };
    return { gid: resolved?.gid ?? null, builtIn: resolved?.step === 'default' };
  };

  const categoryOwner: ShopifyFieldOwner = current.shopifyCategoryOwner ?? summary?.owners.category ?? 'motzu';
  const typeOwner: ShopifyFieldOwner = current.shopifyProductTypeOwner ?? summary?.owners.productType ?? 'motzu';

  const mapped = slugs.filter((slug) => Boolean(categoryMap[slug]?.trim())).length;
  const validRules = rules.filter((rule) => rule.keywords.some((kw) => kw.trim()) && rule.productType.trim()).length;
  const neutralName = (() => {
    if (unmappedCategoryIsUnset(current, flags.unmappedUnsetOn)) return 'not set, Shopify keeps its own';
    const gid = unmappedFallbackGid(current) || categoryDefaults?.neutralGid;
    return gid ? taxonomyShortLabel(taxonomyLabel(taxonomyOptions, gid)) : 'Cameras & Optics';
  })();
  const fallbackShort = mode === 'flag'
    ? 'Type flagged [UNMAPPED].'
    : mode === 'default' ? `Type: ${current.defaultProductType?.trim() || 'not set'}.` : 'Type from the category name.';

  const steps: StepSummary[] = [
    ...(flags.taxonomyOn
      ? [{
        step: 1 as const,
        label: 'Store Taxonomy',
        count: registry ? `· ${taxonomyStats?.leaves ?? 0}` : '· none',
        hint: 'Store SKU picks an entry. Sets both Shopify fields.',
      }]
      : []),
    { step: 2, label: 'Name Rules', count: `· ${validRules}`, hint: flags.ruleCategoryOn ? 'Keyword in the product name.' : 'Keyword in the product name, checked after the overrides.', optional: true },
    { step: 3, label: 'Motzu Classification Overrides', count: `· ${mapped}/${slugs.length}`, hint: 'The product’s Motzu Classification.' },
    { step: 4, label: 'Unmatched Product Rules', count: '', hint: `${fallbackShort} Category: ${neutralName}.` },
  ];
  const stepNumber = (step: 1 | 2 | 3 | 4) => (flags.taxonomyOn ? step : step - 1);

  let subline: string | null = null;
  if (summary && summary.total > 0) {
    const needs = summary.bySource.store_fallback + summary.bySource.neutral;
    subline = `${Math.max(summary.total - needs, 0)} of ${summary.total} synced products categorised · ${needs === 0 ? 'none need a look' : `${needs} need a look`}`;
  } else if (summary) {
    subline = 'No products are selected to sync yet';
  }

  // The menu: one item per panel, each with the live line that says where it stands.
  const unmatchedCount = slugs.length - mapped;
  const M = 'integrations.shopifyMapping.menu';
  const categoryMapStatus: Pick<MappingMenuItem, 'status' | 'tone'> = suggestionCount > 0
    ? { status: t(`${M}.status.suggestions`, { count: suggestionCount }), tone: 'info' }
    : unmatchedCount > 0
      ? { status: t(`${M}.status.unmatched`, { count: unmatchedCount }), tone: 'warn' }
      : { status: t(`${M}.status.allMapped`) };
  const fallbackStatus = mode === 'flag'
    ? t(`${M}.status.fallbackFlag`)
    : mode === 'default'
      ? t(`${M}.status.fallbackDefault`, { type: current.defaultProductType?.trim() || t(`${M}.status.notSet`) })
      : t(`${M}.status.fallbackDerive`);
  const typeItems: MappingMenuItem[] = [
    {
      id: 'owners',
      label: flags.ownershipOn ? t(`${M}.items.owners`) : t(`${M}.items.decisionOrder`),
      status: flags.ownershipOn
        ? t(`${M}.status.owners`, { category: t(OWNER_SHORT_KEY[categoryOwner]), type: t(OWNER_SHORT_KEY[typeOwner]) })
        : t(`${M}.status.steps`, { count: steps.length }),
    },
    ...(flags.taxonomyOn
      ? [{
        id: 'taxonomy' as const,
        label: t(`${M}.items.taxonomy`),
        step: stepNumber(1),
        status: registry ? t(`${M}.status.taxonomyEntries`, { count: taxonomyStats?.leaves ?? 0 }) : t(`${M}.status.taxonomyNotUsed`),
      }]
      : []),
    {
      id: 'name-rules',
      label: t(`${M}.items.nameRules`),
      step: stepNumber(2),
      status: validRules > 0 ? t(`${M}.status.rules`, { count: validRules }) : t(`${M}.status.noRules`),
    },
    { id: 'category-map', label: t(`${M}.items.categoryMap`), step: stepNumber(3), ...categoryMapStatus },
    { id: 'fallback', label: t(`${M}.items.fallback`), step: stepNumber(4), stepTone: 'caution', status: fallbackStatus },
  ];
  const groups: MappingMenuGroup[] = [
    { id: 'type', label: t(`${M}.groups.type`), items: typeItems },
    ...(extraSections.length > 0
      ? [{ id: 'fields', label: t(`${M}.groups.fields`), items: extraSections.map((extra) => extra.item) }]
      : []),
  ];
  // Store Taxonomy is only a section while its flag is on; a link to it opens Field ownership instead.
  const shown: MappingSection = typeItems.some((item) => item.id === active) || extraSections.some((extra) => extra.item.id === active)
    ? active
    : 'owners';
  const typeShown = isTypeCategorySection(shown);

  const tryProduct = (variant: 'side' | 'wide') => (
    <TryProduct
      variant={variant}
      inputs={tryInputs}
      onInputsChange={setTryInputs}
      layout={current}
      slugs={slugs}
      taxonomyOn={flags.taxonomyOn}
      hasRegistry={Boolean(registry)}
      nameRuleCategory={flags.ruleCategoryOn}
      categoryDefaults={categoryDefaults}
      options={taxonomyOptions}
      categoryOwner={flags.ownershipOn ? categoryOwner : undefined}
      typeOwner={flags.ownershipOn ? typeOwner : undefined}
      unmappedCategoryUnset={flags.unmappedUnsetOn}
    />
  );

  /** A panel stays mounted while another shows, so its filters, selection and open editors survive the switch. */
  const labelOf = (id: MappingSection) =>
    [...typeItems, ...extraSections.map((extra) => extra.item)].find((item) => item.id === id)?.label ?? id;
  /** The heading focus moves to on a switch; Field ownership's own visible title serves as its heading. */
  const panelHeading = (id: MappingSection) => (
    <Typography component="h2" id={panelHeadingId(id)} tabIndex={-1} sx={{ ...visuallyHiddenSx, outline: 'none' }}>
      {labelOf(id)}
    </Typography>
  );
  /** A panel stays mounted while another shows, so its filters, selection and open editors survive the switch. */
  const panel = (id: MappingSection, children: React.ReactNode) => (
    <Box
      data-testid={`mapping-panel-${id}`}
      sx={{ display: shown === id ? 'flex' : 'none', flexDirection: 'column', gap: 2 }}
    >
      {id !== 'owners' && panelHeading(id)}
      {children}
    </Box>
  );

  return (
    <Box ref={rowRef} sx={{ scrollMarginTop: 16 }}>
      <MappingSectionLayout groups={groups} active={shown} onSelect={open} aria-label={t(`${M}.ariaLabel`)}>
        <Box ref={contentRef}>
        <Box sx={{ display: typeShown ? 'flex' : 'none', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Box sx={{ flex: '2 1 400px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {panel('owners', (
                <>
                  <SummaryCard
                    subline={subline}
                    sublineLoading={data.summaryLoading}
                    sublineError={data.summaryError ? (data.summaryError instanceof Error ? data.summaryError.message : 'Failed to load the category summary') : null}
                    discovering={discovering}
                    onDiscover={discover}
                    ownersShown={flags.ownershipOn}
                    categoryOwner={categoryOwner}
                    typeOwner={typeOwner}
                    onCategoryOwner={(owner) => patch({ shopifyCategoryOwner: owner })}
                    onTypeOwner={(owner) => patch({ shopifyProductTypeOwner: owner })}
                    steps={steps}
                    onOpenStep={(step) => open(STEP_SECTION[step], { focusPanel: true })}
                    headingId={panelHeadingId('owners')}
                  />
                  <ExampleHint
                    title="How mapping decides a product's category"
                    expanded={explainerOpen}
                    onToggle={(nextOpen) => {
                      setExplainerOpen(nextOpen);
                      writeExplainerOpen(integrationId, nextOpen);
                    }}
                  >
                    <MappingExplainer taxonomyOn={flags.taxonomyOn} ruleCategoryOn={flags.ruleCategoryOn} />
                  </ExampleHint>
                </>
              ))}

              {flags.taxonomyOn && panel('taxonomy', (
                <TaxonomyStep
                  n={stepNumber(1)}
                  registry={registry}
                  stats={taxonomyStats}
                  loading={data.taxonomyLoading}
                  taxonomyOptions={taxonomyOptions}
                  typeOptions={typeOptions}
                  integrationId={integrationId}
                  drafts={taxonomyDrafts}
                  onDraftsChange={onTaxonomyDraftsChange}
                  indexRows={indexRows}
                />
              ))}

              {panel('name-rules', (
                <NameRulesStep
                  n={stepNumber(2)}
                  rules={rules}
                  onChange={(next) => patch({ productTypeRules: next })}
                  typeOptions={typeOptions}
                  categoryEnabled={flags.ruleCategoryOn}
                  taxonomyOptions={taxonomyOptions}
                  keywordSuggestions={keywordSuggestions}
                  matchCounts={ruleCounts}
                />
              ))}

              {panel('category-map', (
                <>
                  {suggestionCount > 0 && !bannerDismissed && (
                    <DiscoveryBanner
                      suggestionCount={suggestionCount}
                      onAcceptAll={() => acceptSuggestions()}
                      onDismiss={() => {
                        setBannerDismissed(true);
                        if (filter === 'suggested') setFilter('all');
                      }}
                    />
                  )}
                  <CategoryMapStep
                    n={stepNumber(3)}
                    slugs={slugs}
                    lensFocalRange={flags.lensFocalRangeOn}
                    categoryMap={categoryMap}
                    taxonomyMap={taxonomyMap}
                    suggestions={suggestions}
                    discovered={discovered !== null}
                    filter={filter}
                    onFilter={setFilter}
                    typeOptions={typeOptions}
                    taxonomyOptions={taxonomyOptions}
                    defaultFor={defaultFor}
                    fallbackText={(slug) => fallbackLabel(slug, mode, current.defaultProductType)}
                    typeNeverTouch={typeOwner === 'never' && flags.ownershipOn}
                    mapSwitchedOff={current.useDiscoveredMapping === false}
                    onApplyMap={() => patch({ useDiscoveredMapping: true })}
                    onSetType={setType}
                    onSetCategory={setCategory}
                    onBulk={applyBulk}
                    onAcceptSuggestion={(slug) => acceptSuggestions(slug)}
                    onResetCategories={resetCategories}
                    newSlugOptions={newSlugOptions}
                    onAddSlug={addSlug}
                  />
                  {/* The table takes the full width here, so the tester moves under it. */}
                  {shown === 'category-map' && tryProduct('wide')}
                </>
              ))}

              {panel('fallback', (
                <FallbackStep
                  n={stepNumber(4)}
                  mode={mode}
                  onMode={setMode}
                  defaultProductType={current.defaultProductType || ''}
                  onDefaultProductType={(value) => onChange(withFallbackMode(current, 'default', value))}
                  typeOptions={typeOptions}
                  unmappedGid={current.unmappedTaxonomyCategory || ''}
                  neutralGid={categoryDefaults?.neutralGid}
                  taxonomyOptions={taxonomyOptions}
                  onUnmappedGid={(gid) => patch({ unmappedTaxonomyCategory: gid || undefined })}
                  allowUnset={flags.unmappedUnsetOn}
                  loadFailed={data.categoryDefaultsFailed}
                />
              ))}
            </Box>

            {/* Try a product stays in view beside the panel being edited, and updates as it changes. */}
            {shown !== 'category-map' && (
              <Box
                id={TRY_ANCHOR_ID}
                sx={{ flex: '1 1 280px', minWidth: 0, position: 'sticky', top: 16, scrollMarginTop: 16 }}
              >
                {tryProduct('side')}
              </Box>
            )}
          </Box>
        </Box>

        {extraSections.map((extra) => (
          <Box key={extra.item.id} data-testid={`mapping-panel-${extra.item.id}`} sx={{ display: shown === extra.item.id ? 'block' : 'none' }}>
            {panelHeading(extra.item.id)}
            {extra.content}
          </Box>
        ))}
        </Box>
      </MappingSectionLayout>
    </Box>
  );
};

export default TypeCategoryView;

